import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  Edit, 
  Building2, 
  Calendar, 
  Clock, 
  FileText,
  User,
  Star,
  MessageSquare,
  FileIcon,
  Download,
  FileSpreadsheet,
  Presentation,
  FileArchive
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return <FileText className="w-5 h-5 text-red-500" />;
    case 'docx':
    case 'doc':
      return <FileText className="w-5 h-5 text-blue-500" />;
    case 'xlsx':
    case 'xls':
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    case 'pptx':
    case 'ppt':
      return <Presentation className="w-5 h-5 text-orange-500" />;
    case 'zip':
      return <FileArchive className="w-5 h-5 text-yellow-600" />;
    default:
      return <FileIcon className="w-5 h-5 text-slate-500" />;
  }
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export const InterventionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntervention();
  }, [id]);

  const fetchIntervention = async () => {
    try {
      const response = await axios.get(`${API}/interventions/${id}`);
      setIntervention(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { styles: 'bg-orange-100 text-orange-700 border-orange-200', label: 'En attente' },
      validated: { styles: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Validée' },
      rejected: { styles: 'bg-red-100 text-red-700 border-red-200', label: 'Rejetée' }
    };
    const { styles, label } = config[status];
    return (
      <Badge variant="outline" className={`${styles} text-base px-4 py-1`}>
        {label}
      </Badge>
    );
  };

  const downloadAttachment = async (attachment) => {
    try {
      // Use the API endpoint for download
      const response = await axios.get(
        `${API}/interventions/${id}/attachments/${attachment.id}/download`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: attachment.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback to base64 if API fails
      if (attachment.data) {
        const byteCharacters = atob(attachment.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: attachment.content_type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    }
  };

  const getBackPath = () => {
    if (user?.role === 'admin') return '/admin/interventions';
    if (user?.role === 'consultant') return '/consultant/interventions';
    return '/pme/pending';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!intervention) return null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 fade-in" data-testid="intervention-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(getBackPath())}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          {user?.role === 'consultant' && intervention.status === 'pending' && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/consultant/interventions/${id}/edit`)}
              data-testid="edit-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
        </div>

        {/* Main Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  {intervention.intervention_type}
                </CardTitle>
                <p className="text-slate-500 mt-1">chez {intervention.sme_company_name}</p>
              </div>
              {getStatusBadge(intervention.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Entreprise</p>
                  <p className="text-slate-900">{intervention.sme_company_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Consultant</p>
                  <p className="text-slate-900">{intervention.consultant_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Date</p>
                  <p className="text-slate-900">{intervention.date}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Clock className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Durée</p>
                  <p className="text-slate-900">{intervention.duration_hours} heures</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h3 className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Description</h3>
              <p className="text-slate-700 leading-relaxed">{intervention.description}</p>
            </div>

            {/* Notes */}
            {intervention.notes && (
              <div>
                <h3 className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Notes</h3>
                <p className="text-slate-700 leading-relaxed">{intervention.notes}</p>
              </div>
            )}

            {/* Attachments */}
            {intervention.attachments && intervention.attachments.length > 0 && (
              <div>
                <h3 className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-3">
                  Pièces jointes ({intervention.attachments.length})
                </h3>
                <div className="space-y-2">
                  {intervention.attachments.map((attachment) => (
                    <div 
                      key={attachment.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(attachment.filename)}
                        <div>
                          <span className="text-sm text-slate-700 font-medium">{attachment.filename}</span>
                          <span className="text-xs text-slate-400 ml-2">
                            ({formatFileSize(attachment.size)})
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadAttachment(attachment)}
                        data-testid={`download-${attachment.id}`}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Télécharger
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Card */}
        {intervention.feedback && (
          <Card className="border-slate-200 shadow-sm" data-testid="feedback-section">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Retour de la PME
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Ratings */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Global', value: intervention.feedback.rating },
                  { label: 'Efficacité', value: intervention.feedback.effectiveness },
                  { label: 'Communication', value: intervention.feedback.communication },
                  { label: 'Professionnalisme', value: intervention.feedback.professionalism },
                ].map((item) => (
                  <div key={item.label} className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 font-medium uppercase mb-1">{item.label}</p>
                    <div className="flex items-center justify-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < item.value ? 'fill-orange-400 text-orange-400' : 'text-slate-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Recommanderait :</span>
                <Badge 
                  variant="outline" 
                  className={intervention.feedback.would_recommend 
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                    : 'bg-red-100 text-red-700 border-red-200'
                  }
                >
                  {intervention.feedback.would_recommend ? 'Oui' : 'Non'}
                </Badge>
              </div>

              {intervention.feedback.strengths && (
                <div>
                  <h4 className="text-sm text-slate-500 font-medium mb-1">Points forts</h4>
                  <p className="text-slate-700">{intervention.feedback.strengths}</p>
                </div>
              )}

              {intervention.feedback.improvements && (
                <div>
                  <h4 className="text-sm text-slate-500 font-medium mb-1">Points à améliorer</h4>
                  <p className="text-slate-700">{intervention.feedback.improvements}</p>
                </div>
              )}

              {intervention.feedback.additional_comments && (
                <div>
                  <h4 className="text-sm text-slate-500 font-medium mb-1">Commentaires additionnels</h4>
                  <p className="text-slate-700">{intervention.feedback.additional_comments}</p>
                </div>
              )}

              <p className="text-xs text-slate-400">
                Soumis par {intervention.feedback.submitted_by_name} le {new Date(intervention.feedback.submitted_at).toLocaleDateString('fr-FR')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Validate Button for SME */}
        {user?.role === 'sme' && intervention.status === 'pending' && (
          <div className="flex justify-center">
            <Button 
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => navigate(`/pme/feedback/${id}`)}
              data-testid="provide-feedback-btn"
            >
              Donner mon avis et valider
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};
